import React, { useEffect, useState, useCallback, useRef } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { parse } from "query-string";
import { SovosPageLayout, SovosPageContentGeneric } from "mosaic-react";
import I18n from "../../I18n";
import { mapProperties, parseFilter } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    downloadAction,
    removeModal,
    upgradeAccount,
    handleDownloadFilesAction,
    getOrganizationDashboardSummary,
    requestDataForOrganizationsAction,
} from "../../store/actions";

import { LoadingIndicator } from "colbi_web_ui/lib/components";
import proxy from "../../routes/proxy";
import { useAuth } from "react-oidc-context";
import styles from "./OrganizationsProjects.module.sass";

import { UserRoles } from "../../enums/userRoles";

import { BuildSovosNavProps, decodeUserSession, redirectNotValid } from "../../utils/utils";
import { Dashboard } from "mosaic-react-icons";
import { mapProjectsByOrganization, selectObjectById } from "../../utils/mapProjectsByOrganization";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import { getDefaultYearType } from "../../components/AggregationData/YearSelector";
import { checkAccessByRole } from "../../utils/roleValidatorUtil";
import AggregateDataWidget from "../../components/AggregationData/AggregationDataWidget";

import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import OrganizationalReportButton from "../../components/AggregationData/OrganizationalReportButton";
import { getLocale } from "../../utils/locale";
import ModalHeaderCloseCustom from "../../components/ModalHeaderCloseCustom/ModalHeaderCloseCustom";
import { getConfigVariable } from "../../config/config";
import PeriodsHeader from "../../components/AggregationData/PeriodsHeader";
import { mapSelectedCalendarType } from "../../utils/date";
import useUpdateThemes from "../../hooks/useUpdateThemes";

const OrganizationsProjects = (props) => {
    const {
        modules,
        user,
        fetch,
        match,
        history,
        location,
        pushModal,
        pushDialog,
        route,
        upgradeAccount,
        getOrganizationDashboardSummary,
        requestDataForOrganizations,
        reload,
    } = props;

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };
    const { results: fetchResults, status: fetchStatus } = fetch || {};
    const routeQueries = (route.queries && route.queries(params)) || [];

    const organizationId = match.params.organizationId;
    const auth = useAuth();
    const dispatch = useDispatch();

    const locale = getLocale(auth, match, true);

    const i18n = I18n.use(locale);
    const userInformation = decodeUserSession(user);
    const ssoEmailSent = window.localStorage.getItem("ssoEmailSent") === "true";

    const [isModalTicketOpen, setIsModalTicketOpen] = useState(false);

    // eslint-disable-next-line
    const [emailSent, _] = useState(ssoEmailSent);

    const [selectedYearType, setSelectedYearType] = useState(getDefaultYearType(params.filter));
    const [selectedYear, setSelectedYear] = useState(null);

    const checkUpgrade = useCallback(() => {
        if (
            pushDialog &&
            userInformation &&
            !userInformation.hasSovosIdentity &&
            !userInformation.hasUpgradedAccount &&
            organizationId &&
            !emailSent
        ) {
            pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                title: i18n`sso_upgrade_title`,
                alert: true,
                cancelEvent: false,
                text: [
                    i18n`sso_upgrade_text`,

                    i18n`sso_upgrade_transition`,

                    i18n`sso_upgrade_confirm`,
                ],
            })
                .then(() => {
                    upgradeAccount().then(({ message }) => {
                        window.localStorage.setItem("ssoEmailSent", "true");
                        pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                            title: message,

                            text: [
                                i18n`sso_account_upgraded`,

                                i18n`sso_account_upgraded_email`,

                                i18n`sso_account_upgraded_grateful`,
                            ],

                            alert: true,

                            cancel: () => {},
                        })
                            .then(() => null)

                            .catch(() => null);
                    });
                })

                .catch(() => null);
        } else if (
            pushDialog &&
            auth &&
            !auth?.isLoading &&
            !auth?.isAuthenticated &&
            userInformation &&
            userInformation.hasSovosIdentity &&
            !userInformation.hasUpgradedAccount &&
            organizationId &&
            !emailSent
        ) {
            pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                title: i18n`sso_upgrade_header`,

                text: [
                    i18n`sso_confirmation_reminder`,

                    i18n`sso_confirmation_reprompt`,

                    i18n`sso_confirmation_reprompt_warning`,
                ],
            })
                .then(() => {
                    window.localStorage.setItem("ssoEmailSent", "true");
                    upgradeAccount().then(({ message }) => {
                        pushDialog("MessageDialog", i18n`sso_upgrade_header`, {
                            title: message,

                            text: [
                                i18n`sso_account_upgraded`,

                                i18n`sso_account_upgraded_email`,

                                i18n`sso_account_upgraded_grateful`,
                            ],

                            alert: true,

                            cancel: () => {},
                        })
                            .then(() => null)

                            .catch(() => null);
                    });
                })

                .catch(() => {
                    window.localStorage.setItem("ssoEmailSent", "true");
                    return null;
                });
        }
    }, [pushDialog, userInformation, auth, i18n, organizationId, upgradeAccount, emailSent]);

    useEffect(() => {
        if (
            userInformation &&
            checkAccessByRole(userInformation, [UserRoles.PP_SUPPORT, UserRoles.SUPPORT]) &&
            !isModalTicketOpen
        ) {
            setIsModalTicketOpen(true);
            pushModal({
                title: "Ticket Submission",
                data: {},
                args: "",
                view: "TicketForm",
                modalType: "center",
                customHeaderButton: (props) => (
                    <ModalHeaderCloseCustom
                        {...props}
                        title={"Ticket Submission"}
                        closeButtonAction={() => dispatch(logout(auth))}
                    />
                ),
            }).catch(() => null);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(userInformation)]);

    const checkUpgradeRef = useRef(checkUpgrade);

    useEffect(() => {
        if (getConfigVariable("REACT_APP_AUTH_MODE") === "SSO") {
            checkUpgradeRef.current();
        }
    }, [locale, i18n, auth?.isLoading]);

    //Data for menu
    const { menuOrganizationProject = [] } = mapProperties(
        {
            menuOrganizationProject: "query(0).list",
        },
        fetch.results,
        fetch.status,
        OrganizationsProjects.queries(params),
        props,
        params,
        i18n,
        locale,
    );

    //results for the list
    const {
        organizationProjects,
        filter,
        status: organizationProjectQueryStatus,
    } = mapProperties(
        {
            organizationProjects: "query(0).organizationProject",
            filter: "param(filter)",
            status: "status(0)",
        },
        fetch.results,
        fetch.status,
        route.queries(params),
        props,
        params,
        i18n,
        locale,
    );

    const { loading = true, success = false } = organizationProjectQueryStatus || {};

    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    const organizationModules =
        fetch.results[`${locale}_get_organization_cache_organizationId_${organizationId}`]
            ?.modules || [];

    const { maxFiscalYear: organizationMaxFiscalYear } = organizationProjects || {};

    const organizationSelectedInMenu = selectObjectById(menuOrganizationProject, organizationId);

    const { accounts, selectedAccount } = cleanPropertiesNestedInMenu(
        menuOrganizationProject,
        organizationSelectedInMenu,
    );

    const isLoading =
        (fetch.activity === null || fetch.activity) &&
        !organizationId &&
        !selectedYearType &&
        !selectedYear;

    // Apply theme configuration if mosaicThemeConfig exists in organization data
    useEffect(() => {
        if (organizationProjects && organizationProjects.mosaicThemeConfig) {
            updateThemes(organizationProjects.mosaicThemeConfig);
        }
    }, [organizationProjects, updateThemes]);

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    if (redirect) {
        return redirect;
    }

    if (
        !organizationId &&
        userInformation.organizationsRoles &&
        !checkAccessByRole(userInformation, [UserRoles.PP_SUPPORT, UserRoles.SUPPORT])
    ) {
        const orgs = Object.keys(userInformation.organizationsRoles || {});
        return <Redirect to={`/${locale}/organizations/${orgs.length ? orgs[0] : ""}`} />;
    }

    location.state = {
        ...location.state,
        selectedLink: i18n("projects"),
    };

    const link = {
        label: i18n("projects"),
        icon: Dashboard,
        action: () => {},
    };
    const staticLinks = [link];

    const navProps = BuildSovosNavProps(
        i18n("projects"),
        accounts,
        selectedAccount,
        { locale, history, proxy, params },
        userInformation,
        staticLinks || [],
    );
    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries: [],
        params,
        propsFiscalYear: "",
        propsImportId: "",
        repositoryStatus: "",
        styles,
    };

    const showPeriodsColumn = (organizationModules || []).find(
        (element) => element === "aggregate_org_data",
    );

    const heatMapLabel = showPeriodsColumn
        ? [
              {
                  headerName: i18n`${selectedYearType?.name} ${selectedYear?.name}`,
                  label: ["aggregationDataResult"],
                  renderHeader: (params) => (
                      <PeriodsHeader
                          yearTypeName={selectedYearType?.name}
                          yearName={selectedYear?.name}
                      />
                  ),
                  sortable: false,
                  type: "customComponent",
                  align: "center",
              },
          ]
        : [];

    const projectsToShowInTheList =
        mapProjectsByOrganization(
            organizationProjects || {},
            undefined,
            selectedYear?.name,
            mapSelectedCalendarType(selectedYearType?.name),
            organizationModules,
        ) || [];

    const header = [
        {
            headerName: `${i18n`TRN`} - ${i18n`Project`}`,
            label: ["name"],
            basis: "15%",
            type: "string",
            sortable: false,
        },
        {
            label: ["status"],
            headerName: `Status`,
            type: "status",
            sortable: false,
            basis: "8%",
        },
        {
            label: ["period"],
            headerName: `Fiscal Years`,
            align: "right",
            type: "string",
            sortable: false,
            basis: "8%",
        },
        {
            label: ["active"],
            headerName: "Active",
            type: "status",
            sortable: false,
            basis: "5%",
        },
        ...heatMapLabel,
    ];

    const children = route.components.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            fetchResults,
            fetchStatus,
            routeQueries,
            props,
            params,
            i18n,
            locale,
        );
        const componentProps = {
            ...componentParsedProps,
            modules,
            header: header,
            items: projectsToShowInTheList,
            toolbarProps: {
                data: projectsToShowInTheList || [],
                alternativeQueryData: [
                    {
                        resource: "organization_projects_export",
                        args: {
                            locale: `${locale}`,
                            organizationId: `${organizationId}`,
                            organizationMaxFiscalYear:
                                selectedYear?.name || `${selectedYear?.name}`,
                            filter: params.filter
                                ? {
                                      type: "[FilterInput!]",
                                      value: parseFilter(params.filter),
                                  }
                                : undefined,
                            yearType: mapSelectedCalendarType(selectedYear?.name),
                        },
                        body: `report`,
                        downloadable: true,
                        downloadOnly: true,
                        xlsxTranslation: "organizations_projects",
                    },
                ],
            },
        };

        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    });

    return LoadingComponent ? (
        LoadingComponent
    ) : (
        <GlobalLayout {...globalLayoutProps}>
            <SovosPageLayout 
                pageTitle={organizationProjects?.name || ""}
                titleBarActions={
                    organizationModules.includes("aggregate_org_data")
                        ? [
                              <OrganizationalReportButton
                                  key="organizationalReportButton"
                                  pushModal={pushModal}
                                  i18n={i18n}
                                  organizationId={organizationId}
                                  reload={reload}
                                  pushDialog={pushDialog}
                                  user={userInformation}
                                  requestDataForOrganizations={requestDataForOrganizations}
                              />,
                          ]
                        : []
                }
            >
                <SovosPageContentGeneric>
                    {isLoading ? (
                        <LoadingIndicator className={styles["loading-indicator"]} />
                    ) : (
                        <div className={styles["org-heat-map-container"]}>
                            <AggregateDataWidget
                                getOrganizationDashboardSummary={getOrganizationDashboardSummary}
                                organizationId={organizationId}
                                selectedYearType={selectedYearType}
                                selectedYear={selectedYear}
                                setSelectedYearType={setSelectedYearType}
                                setSelectedYear={setSelectedYear}
                                params={params}
                                organizationModules={organizationModules}
                                i18n={i18n}
                                organizationMaxFiscalYear={organizationMaxFiscalYear}
                                filter={filter}
                            />
                            {children}
                        </div>
                    )}
                </SovosPageContentGeneric>
            </SovosPageLayout>
        </GlobalLayout>
    );
};

OrganizationsProjects.queries = (param) => {
    return [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "get_organization_cache",
            body: "modules",
            args: {
                organizationId: param.organizationId,
            },
        },
    ];
};

export default connect(
    ({ user, fetch, uploads }) => ({
        user,
        fetch,
        uploads,
    }),
    {
        logout,
        pushDialog,
        pushModal,
        uploadFiles: uploadAction,
        download: downloadAction,
        downloadFile: handleDownloadFilesAction,
        submitAuditNote: auditNoteSubmit,
        requestDataForOrganizations: requestDataForOrganizationsAction,
        popModal,
        toggleMenu,
        setLocale,
        removeModal,
        upgradeAccount,
        getOrganizationDashboardSummary,
    },
)(withRouter(OrganizationsProjects));
